/**
 * `choice` — the quiz primitive, and the block that carries the most weight.
 *
 * Three things it has to get right:
 *
 *  1. **Size.** These are the controls the POC rendered as ~24px native buttons. Here every option
 *     is a 56px-minimum full-width button with a letter chip, a pressed state, and distinct
 *     correct / incorrect / here-is-the-answer states.
 *  2. **Diagnosis.** *Which* wrong answer she picks is the actionable signal — "it gets tired" is
 *     animism, "it forgets it was moving" is a different misconception needing a different
 *     follow-up. The `misconception` tag rides along on the `answer` event automatically.
 *  3. **No penalty framing.** Feedback is "not quite — try another" and the block stays playable.
 *     Nothing is scored at her, nothing is locked.
 */

import { append, el, seededShuffle } from "../../dom.js";
import { track } from "../../telemetry.js";
import { blockShell, liveRegion, type RenderContext } from "../context.js";
import { addSpeaker } from "../../speech/reader.js";
import { joinSpoken, lettered } from "../../speech/say.js";
import type { AnswerPayload } from "../../../shared/api.js";
import type { ChoiceBlock } from "../../../lesson/spec.js";

const LETTERS = ["A", "B", "C", "D"] as const;

export function renderChoice(block: ChoiceBlock, ctx: RenderContext): HTMLElement {
  const shell = blockShell("choice", block.id);
  if (block.review) {
    shell.appendChild(
      el("p", { class: "review-flag", text: "Remember this one?" }),
    );
  }

  const promptId = `${block.id}-prompt`;
  shell.appendChild(el("h3", { class: "block-prompt", text: block.prompt, attrs: { id: promptId } }));

  const list = el("ul", {
    class: "choice-list",
    attrs: { role: "group", "aria-labelledby": promptId },
  });

  // Options are presented in a stable shuffled order: the answer key must not be guessable from
  // position, but the order must not change under her while she reads.
  const options = seededShuffle(block.options, `choice:${block.id}`);
  const buttons = new Map<string, HTMLButtonElement>();
  let firstAttemptReported = false;
  let attempts = 0;

  const feedback = liveRegion();

  options.forEach((option, index) => {
    const letter = LETTERS[index] ?? String(index + 1);
    const btn = el("button", {
      class: "choice-option",
      attrs: { type: "button", "aria-pressed": "false", "data-option-id": option.id },
    });
    append(btn, [
      el("span", { class: "choice-mark", text: letter, attrs: { "aria-hidden": "true" } }),
      el("span", { class: "choice-label", text: option.label }),
    ]);
    btn.addEventListener("click", () => choose(option.id));
    buttons.set(option.id, btn);
    list.appendChild(el("li", {}, [btn]));
  });

  shell.appendChild(list);
  shell.appendChild(feedback);
  // On-screen order and letters, then whatever feedback is showing when she taps.
  addSpeaker(
    shell,
    () => joinSpoken([block.prompt, ...lettered(options.map((o) => o.label)), feedback.textContent]),
    block.id,
  );

  function choose(optionId: string): void {
    const option = block.options.find((o) => o.id === optionId);
    if (!option) return;
    attempts += 1;
    const correct = option.correct === true;

    // Visual state. Colour alone never carries it: the chip glyph changes too.
    for (const [id, btn] of buttons) {
      btn.classList.remove("is-correct", "is-incorrect", "is-answer", "is-muted");
      btn.setAttribute("aria-pressed", id === optionId ? "true" : "false");
    }
    const chosenBtn = buttons.get(optionId);
    if (chosenBtn) {
      chosenBtn.classList.add(correct ? "is-correct" : "is-incorrect");
      const mark = chosenBtn.querySelector(".choice-mark");
      if (mark) mark.textContent = correct ? "✓" : "!";
    }

    if (correct) {
      // Once she has it, the rest go quiet rather than being disabled — a disabled control that
      // was tappable a second ago reads as breakage.
      for (const [id, btn] of buttons) if (id !== optionId) btn.classList.add("is-muted");
      feedback.className = "feedback feedback--good";
      feedback.textContent = option.feedback ?? "That's it.";
    } else {
      feedback.className = "feedback feedback--try";
      feedback.textContent = option.feedback ?? "Not quite — have another go.";
      // Reset every chip that is not the current pick, so the board stays readable.
      for (const [id, btn] of buttons) {
        if (id === optionId) continue;
        const mark = btn.querySelector(".choice-mark");
        const index = options.findIndex((o) => o.id === id);
        if (mark) mark.textContent = LETTERS[index] ?? String(index + 1);
      }
      // After a second miss, show where the answer is rather than leave her hunting.
      if (attempts >= 2) {
        const answer = block.options.find((o) => o.correct === true);
        if (answer) buttons.get(answer.id)?.classList.add("is-answer");
      }
    }

    const payload: AnswerPayload = {
      blockId: block.id,
      correct,
      chosen: optionId,
      ...(block.topicId === undefined ? {} : { topicId: block.topicId }),
      ...(option.misconception === undefined ? {} : { misconception: option.misconception }),
      ...(block.review === undefined ? {} : { review: block.review }),
      // No visual treatment for probe, unlike review's "Remember this one?" badge above — the whole
      // premise of the placement probe is that the child never knows she is being checked.
      ...(block.probe === undefined ? {} : { probe: block.probe }),
    };

    if (!firstAttemptReported) {
      // The first attempt is the mastery evidence. Later tries are behaviour, not evidence.
      firstAttemptReported = true;
      track("answer", payload as unknown as Record<string, unknown>);
      ctx.answered(block.id, payload);
    } else {
      track("interact", {
        action: "choice-retry",
        blockId: block.id,
        value: optionId,
        attempt: attempts,
        correct,
      });
      if (correct) ctx.answered(block.id, payload);
    }
  }

  return shell;
}
